import { useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { api, get, post } from "lib/api";
import { isCustomerRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function normalizeEntity(row) {
  if (!row) return null;
  if (row?.data) return normalizeEntity(row.data);
  if (row?.attributes)
    return {
      id: row.id,
      documentId: row.documentId || row.id,
      ...row.attributes,
    };
  return row;
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

async function uploadFiles(fileList) {
  const files = Array.from(fileList || []).filter(Boolean);
  if (!files.length) return [];
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const uploadRes = await api.post("/api/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const rows = Array.isArray(uploadRes?.data) ? uploadRes.data : [];
  return rows
    .map((row) => Number(row?.id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export default function CustomerWarrantyClaimChat() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useStorefrontCopy();
  const isCustomer = isCustomerRole(String(pluginRole || "").toLowerCase());

  const [notice, setNotice] = useState(null);
  const [chatMessage, setChatMessage] = useState("");
  const [chatFiles, setChatFiles] = useState([]);
  const [chatSaving, setChatSaving] = useState(false);

  const claimQuery = useQuery({
    queryKey: ["storefront-warranty-claim-chat-context", documentId],
    queryFn: () => get(`/api/storefront/warranty-claims/${documentId}`),
    enabled: !!token && isCustomer && !!documentId,
    staleTime: 10000,
  });

  const claim = claimQuery.data?.data?.claim || null;
  const supportThread = normalizeEntity(claim?.supportThread);
  const threadDocumentId = String(supportThread?.documentId || "").trim();

  const chatQuery = useQuery({
    queryKey: ["storefront-warranty-claim-chat-page", threadDocumentId],
    queryFn: () => get(`/api/support-chat/threads/${threadDocumentId}`),
    enabled: !!token && isCustomer && !!threadDocumentId,
    staleTime: 5000,
  });

  const messages = useMemo(() => {
    const rows = Array.isArray(chatQuery.data?.data?.messages)
      ? chatQuery.data.data.messages
      : [];
    return rows;
  }, [chatQuery.data]);

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/valve-bodies" replace />;

  const handleChatSend = async () => {
    const trimmed = String(chatMessage || "").trim();
    if (!threadDocumentId) return;
    if (!trimmed && !chatFiles.length) return;
    try {
      setChatSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(chatFiles);
      await post(`/api/support-chat/threads/${threadDocumentId}/messages`, {
        data: {
          message:
            trimmed ||
            `Uploaded ${imageIds.length} image${
              imageIds.length === 1 ? "" : "s"
            } for this claim.`,
          imageIds,
        },
      });
      setChatMessage("");
      setChatFiles([]);
      await chatQuery.refetch();
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to send chat message.",
      });
    } finally {
      setChatSaving(false);
    }
  };

  return (
    <CustomerPortalLayout
      title={
        claim?.rmaNumber
          ? `${claim.rmaNumber} ${t("support.title", "Support")}`
          : t("support.title", "Support")
      }
      subtitle={t(
        "support.subtitle",
        "Message support and share claim images."
      )}
      actions={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() =>
              navigate(
                `/account/warranty-claims/${encodeURIComponent(
                  String(documentId || "")
                )}`
              )
            }
          >
            {t("warranty.backToClaim", "Back to claim")}
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => navigate("/account/valve-bodies")}
          >
            {t("registeredUnits.title", "Registered Items")}
          </MDButton>
        </Box>
      }
    >
      {notice ? (
        <Alert severity={notice.type} sx={{ mb: 2 }}>
          {notice.text}
        </Alert>
      ) : null}
      {claimQuery.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {claimQuery.error?.response?.data?.error?.message ||
            claimQuery.error?.message ||
            "Failed to load warranty claim."}
        </Alert>
      ) : null}
      {claimQuery.isLoading ? (
        <Alert severity="info">Loading claim chat…</Alert>
      ) : !claim ? (
        <Alert severity="warning">Warranty claim not found.</Alert>
      ) : !threadDocumentId ? (
        <Alert severity="info">
          Chat thread is being prepared for this claim.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ p: 2 }}>
          {chatQuery.isLoading ? (
            <Alert severity="info">Loading chat…</Alert>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: 420,
                overflowY: "auto",
                pr: 1,
              }}
            >
              {!messages.length ? (
                <Alert severity="info">
                  No messages yet. Start the conversation below.
                </Alert>
              ) : (
                messages.map((message, index) => (
                  <Card
                    key={String(message?.documentId || index)}
                    variant="outlined"
                    sx={{ p: 1.25 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <MDTypography variant="caption" fontWeight={700}>
                        {message?.senderRole === "staff"
                          ? t("support.staffName", "Support")
                          : t("support.you", "You")}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.7 }}
                      >
                        {formatDateTime(message?.createdAt)}
                      </MDTypography>
                    </Box>
                    <MDTypography
                      variant="body2"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                    >
                      {message?.message || ""}
                    </MDTypography>
                    {Array.isArray(message?.imageUrls) &&
                    message.imageUrls.length ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {message.imageUrls.map((url) => (
                          <MDButton
                            key={`${message?.documentId}-${url}`}
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ px: 0 }}
                          >
                            View image
                          </MDButton>
                        ))}
                      </Box>
                    ) : null}
                  </Card>
                ))
              )}
            </Box>
          )}
          <Divider sx={{ my: 1.5 }} />
          <MDInput
            fullWidth
            multiline
            minRows={2}
            label="Message support team"
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
          />
          <Box sx={{ mt: 1 }}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setChatFiles(Array.from(e.target.files || []))}
            />
          </Box>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              disabled={chatSaving}
              onClick={handleChatSend}
            >
              {chatSaving ? "Sending..." : "Send message"}
            </MDButton>
            {chatFiles.length ? (
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75 }}
              >
                {chatFiles.length} image{chatFiles.length === 1 ? "" : "s"}{" "}
                selected
              </MDTypography>
            ) : null}
          </Box>
        </Card>
      )}
    </CustomerPortalLayout>
  );
}
